/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { FlaggableType, FlagRecord } from "@packages/sdk";
import { useRequestFlagRecord } from "@packages/sdk";
import { Close as RadixUIDialogClose } from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { Divider } from "../../../_base/_core/Divider/Divider.emotion";
import { IconButton } from "../../../_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { LockClosedIcon } from "../../../_base/icons/Icon/GeneralIcons";
import {
  CloseIcon,
  ExportIcon,
} from "../../../_base/icons/Icon/NavigationIcons";
import { ButtonLink } from "../../../refactor/ButtonLink/ButtonLink.emotion";
import { TextArea } from "../../forms/TextArea/TextArea.emotion";
import { Form } from "../../forms/wrapper/Form.emotion";
import { FormField } from "../../forms/wrapper/FormField.emotion";
import { FlagRecordReasonRadioInput } from "../FlagRecordReasonRadioInput/FlagRecordReasonRadioInput.emotion";
import { FlagRecordSuccess } from "../FlagRecordSuccess/FlagRecordSuccess.emotion";

const formStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  width: spacing.full,
};
const headerStyle: Interpolation = {
  display: "flex",
  justifyContent: "space-between",
  width: spacing.full,
};
const footerStyle: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "end",
};
const footerMobileStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.background,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  justifyContent: "center",
  left: 0,
  paddingBottom: spacing.ms,
  paddingInline: spacing.ml,
  position: "fixed",
  width: spacing.full,
});
const formStepContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  [cssMediaQueryMinWidthTablet]: { width: "576px" },
};
const commentSublabelStyle: Interpolation = {
  alignItems: "center",
  display: "inline-flex",
  gap: spacing.s,
};
const inactiveStyle: Interpolation = {
  display: "none",
};
const buttonContainerStyle: Interpolation = {
  display: "flex",
};

enum FlagRecordFormStep {
  Reason,
  Comment,
  Success,
}

type FlagRecordFormProps = {
  flaggableId: number;
  flaggableType: FlaggableType;
};

export const FlagRecordForm = ({
  flaggableId,
  flaggableType,
}: FlagRecordFormProps) => {
  const analytics = useAnalytics();
  const t = useTranslations();
  const isDesktop = useIsDesktopViewport();
  const [step, setStep] = useState<FlagRecordFormStep>(
    FlagRecordFormStep.Reason,
  );

  const { mutationPost: flagRecordMutation } = useRequestFlagRecord({
    onSuccess: () => setStep(FlagRecordFormStep.Success),
  });

  const form = useForm<FlagRecord>();

  useEffect(() => {
    if (step === FlagRecordFormStep.Reason) {
      analytics.track({
        event: HallowAnalyticsEvent.ViewedScreen,
        properties: {
          screen_name: "report_reason",
          type: "campaign",
        },
      });
    } else if (step === FlagRecordFormStep.Comment) {
      analytics.track({
        event: HallowAnalyticsEvent.ViewedScreen,
        properties: {
          screen_name: "report_comment",
          type: "campaign",
        },
      });
    }
  }, [step]);

  const onSubmit = (data: FlagRecord) => {
    flagRecordMutation.mutate({
      flaggable_id: flaggableId,
      flaggable_type: flaggableType,
      ...data,
    });
  };

  return (
    <Form
      formCSS={formStyle}
      formProps={{
        onSubmit: form.handleSubmit(onSubmit),
      }}
      {...form}
    >
      {step !== FlagRecordFormStep.Success && (
        <div css={headerStyle}>
          <Text type="headline" size="m">
            {t("community_flag_submit_report")}
          </Text>
          {isDesktop && (
            <RadixUIDialogClose asChild>
              <IconButton icon={<CloseIcon />} variant="transparentPrimary" />
            </RadixUIDialogClose>
          )}
        </div>
      )}
      <div
        css={[
          formStepContainerStyle,
          step === FlagRecordFormStep.Success && inactiveStyle,
        ]}
      >
        {step === FlagRecordFormStep.Reason && (
          <>
            <Text type="body" size="m">
              {t("community_flag_familiar_guidelines")}
            </Text>
            <div css={buttonContainerStyle}>
              <ButtonLink
                size="s"
                linkProps={{
                  href: "https://hallow.com/community-guidelines/",
                  target: "_blank",
                }}
                buttonProps={{ endIcon: <ExportIcon /> }}
              >
                {t("dialog_report_guidelines_button")}
              </ButtonLink>
            </div>
            <Divider />
            <Controller
              name="reason"
              control={form.control}
              render={({ field }) => (
                <FlagRecordReasonRadioInput
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
            {form.formState.errors.reason && (
              <span>{t("input_validation_error_required")}</span>
            )}
            <div css={isDesktop ? footerStyle : footerMobileStyle}>
              <Button
                onClick={() => setStep(FlagRecordFormStep.Comment)}
                disabled={!form.watch("reason")}
                size={"l"}
                isFullWidth={!isDesktop}
              >
                {t("community_flag_continue")}
              </Button>
            </div>
          </>
        )}
        {step === FlagRecordFormStep.Comment && (
          <>
            <Text type="body" size="m">
              {t("community_flag_more_detail")}
            </Text>
            <FormField
              control={form.control}
              name="comment"
              description={
                <div css={commentSublabelStyle}>
                  <LockClosedIcon.Filled />
                  <Text>{t("community_flag_shared_with_support")}</Text>
                </div>
              }
              render={({ field }) => (
                <TextArea
                  maxLength={255}
                  placeholder={t("enter_thoughts_here")}
                  rows={4}
                  {...field}
                />
              )}
            />
            <div css={isDesktop ? footerStyle : footerMobileStyle}>
              <Button
                onClick={() => setStep(FlagRecordFormStep.Reason)}
                variant="neutral"
                size={"l"}
                isFullWidth={!isDesktop}
              >
                {t("accessibility_title_go_back")}
              </Button>
              <Button
                type="submit"
                disabled={flagRecordMutation.isPending}
                size={"l"}
                isFullWidth={!isDesktop}
              >
                {t("community_flag_submit_report_action")}
              </Button>
            </div>
          </>
        )}
      </div>

      {step === FlagRecordFormStep.Success && <FlagRecordSuccess />}
    </Form>
  );
};
